import { ALL_SCOPE_SENTINEL } from "@p0security/shared/types/assessment";
import { CredentialNode } from "@p0security/shared/types/assessment/data/credential";
import { Descriptions, Typography } from "@p0security/ui";
import { ScopeContext } from "components/Assessment/contexts/ScopeContext";
import pluralize from "pluralize";
import { useContext } from "react";

import { Timestamp } from "../../../../Timestamp";
import { ConsumersList } from "../../cells/ConsumersList";
import { CredentialDisplay } from "../../cells/Credential";
import { IdentityCell, IdentityLink } from "../../cells/Identity";
import { LastAuthenticated } from "../../cells/LastAuthenticated";
import { LastRotated } from "../../cells/LastRotated";
import {
  RiskGroupedPrivilegeList,
  RiskList,
} from "../../cells/PrivilegeAggregate";
import { NodeDescriptions } from "../NodeDescriptions";

const { Item } = Descriptions;

export const CredentialNodeDisplay: React.FC<{
  node: CredentialNode;
}> = ({ node }) => {
  const { provider, scopeKey } = useContext(ScopeContext);
  const [identity] = node.aggregates.identity;
  const grantCount = node.aggregates.grants.length;
  return (
    <NodeDescriptions>
      <Item label="Credential">
        <CredentialDisplay credential={node.data} id={node.key} />
      </Item>
      <Item label="Principal">
        <IdentityLink data={identity} type="short" show="grant" />{" "}
        <IdentityCell identity={identity} />
      </Item>
      <Item label="Last Used">
        <LastAuthenticated
          identity={identity}
          createdAt={node.data.createdTime}
          last={node.data.lastAuthnTime}
          lookback={node.data.maxAuthnLookbackDays}
        />
      </Item>
      {
        // consumers do not exist on all-target scope
        provider !== "all" && (
          <Item label="Used By">
            <ConsumersList
              consumers={node.aggregates.consumers}
              lastAuthnTime={node.data.lastAuthnTime}
            />
          </Item>
        )
      }
      <Item label="Last Rotated">
        <LastRotated credential={node.data} />
      </Item>
      {node.data.expirationTime && (
        <Item label="Expiration">
          <Timestamp at={node.data.expirationTime} />
        </Item>
      )}
      <Item label="Entitlements">
        <Typography.Paragraph>
          This identity can exercise {grantCount}{" "}
          {pluralize("entitlement", grantCount)}
        </Typography.Paragraph>
        <IdentityLink data={identity} show="identity" />
      </Item>
      {provider && (
        <Item label="Risks">
          {scopeKey === ALL_SCOPE_SENTINEL ? (
            <RiskList risks={node.aggregates.risks} />
          ) : (
            <RiskGroupedPrivilegeList
              privileges={node.aggregates.privileges}
              provider={provider}
              showControl
            />
          )}
        </Item>
      )}
    </NodeDescriptions>
  );
};
